/**
 * CSV exports (TMMAPP-0022, design §11.8). Each call goes through `download()` with
 * `Accept: text/csv`; the filename is the server's `Content-Disposition`. XLSX workbooks stay
 * on the existing account / virtual account / envelope modules, except the tax report, which spans
 * every readable virtual account.
 */
import { civilRangeToInstants } from '@/i18n/zonedCivil';
import { download, type DownloadResult } from './client';

export interface DateWindow {
  from?: string | undefined;
  to?: string | undefined;
}

/**
 * The audit exports' window: civil bounds, sent as date-times in the person's zone (`/me` timezone),
 * `from` at the start of its day and `to` at the start of the next (TMMAPP-0161).
 */
export interface AuditWindow extends DateWindow {
  timeZone: string;
}

const CSV_ACCEPT = 'text/csv';

export const exportsApi = {
  /** GET /v1/me/exports/audit?from&to — owner audit CSV (range only; no action/entity filters). */
  async myAuditCsv(window: AuditWindow): Promise<DownloadResult> {
    return download('/v1/me/exports/audit', {
      query: civilRangeToInstants(window, window.timeZone),
      accept: CSV_ACCEPT,
    });
  },
  /** GET /v1/virtual-accounts/{id}/exports/audit?from&to — protected audit CSV. */
  async virtualAccountAuditCsv(
    virtualAccountId: number,
    window: AuditWindow,
  ): Promise<DownloadResult> {
    return download(`/v1/virtual-accounts/${virtualAccountId}/exports/audit`, {
      query: civilRangeToInstants(window, window.timeZone),
      accept: CSV_ACCEPT,
    });
  },
  /** GET /v1/me/exports/register?accountId&from&to — owner or can_view_full. */
  async myRegisterCsv(query: {
    accountId: number;
    from?: string | undefined;
    to?: string | undefined;
  }): Promise<DownloadResult> {
    return download('/v1/me/exports/register', {
      query: { accountId: query.accountId, from: query.from, to: query.to },
      accept: CSV_ACCEPT,
    });
  },
  /**
   * GET /v1/me/exports/tax-report.xlsx?from&to&virtualAccountId (TMMAPI-0147, MM-D-127) — taxable
   * income, then deductible expenses; every readable virtual account without `virtualAccountId`.
   */
  async taxReportXlsx(
    query: DateWindow & { virtualAccountId?: number | undefined } = {},
  ): Promise<DownloadResult> {
    return download('/v1/me/exports/tax-report.xlsx', {
      query: { from: query.from, to: query.to, virtualAccountId: query.virtualAccountId },
    });
  },
  /** GET /v1/virtual-accounts/{id}/exports/register?from&to — owner or share. */
  async virtualAccountRegisterCsv(
    virtualAccountId: number,
    window: DateWindow = {},
  ): Promise<DownloadResult> {
    return download(`/v1/virtual-accounts/${virtualAccountId}/exports/register`, {
      query: { from: window.from, to: window.to },
      accept: CSV_ACCEPT,
    });
  },
};
